import React, {
    useState,
    useContext,
    createContext,
    PropsWithChildren,
} from 'react'
import { useClipboard } from '@chakra-ui/react'

import { ChatMessageFieldsFragment } from '~graphql/generated/graphql'
import { SetStateFn } from '~config/constants'
import { getCopyMessageText, isSystemMessage } from './helpers'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { useWritebackMessages } from '~utils/hooks/use-writeback-messages'

type ExportMessagesProviderType = {
    isExporting: boolean
    setIsExporting: SetStateFn<boolean>

    selectedMessages: ChatMessageFieldsFragment[]
    setSelectedMessages: (val: ChatMessageFieldsFragment[]) => void

    selectAllMessages: () => void
    selectManualMessages: () => void
    clearSelectedMessages: () => void

    canExport: boolean
    handleCopy: () => void
    handlePimsWriteback: () => void
    cancelExport: () => void
}

const ExportMessagesContext = createContext<ExportMessagesProviderType>(
    {} as ExportMessagesProviderType,
)

interface ExportMessagesProviderProps extends PropsWithChildren {
    /** Currently loaded/visible messages available for export selection. */
    messages: ChatMessageFieldsFragment[]
}

export default function ExportMessagesProvider({
    children,
    messages,
}: ExportMessagesProviderProps) {
    const { onCompleted } = useToastFeedback(
        'Successfully copied messages!',
        'There was a problem copying the messages.',
    )
    const { value, setValue, onCopy } = useClipboard('')

    const [isExporting, setIsExporting] = useState(false)

    const [selectedMessages, setSelectedMessages] = useState<
        ChatMessageFieldsFragment[]
    >([])

    const [writebackMessages, { loading }] = useWritebackMessages()

    function cancelExport() {
        setIsExporting(false)
        setSelectedMessages([])
        setValue('')
    }

    function handleCopy() {
        if (selectedMessages.length === 0) {
            return
        }

        onCopy()
        onCompleted()
        cancelExport()
    }

    function handlePimsWriteback() {
        const chatId = selectedMessages.at(0)?.chat?.id

        if (selectedMessages.length === 0 || !chatId) {
            return
        }

        writebackMessages(chatId, selectedMessages)
        cancelExport()
    }

    function handleSetSelectedMessages(
        nextMessages: ChatMessageFieldsFragment[],
    ) {
        const selectedMessagesText = [...nextMessages]
            .sort((a, b) => a.created_at - b.created_at)
            .map(message => getCopyMessageText(message))
            .join('\n\n')

        setValue(selectedMessagesText)
        setSelectedMessages(nextMessages)
    }

    function selectAllMessages() {
        handleSetSelectedMessages(messages)
    }

    function selectManualMessages() {
        handleSetSelectedMessages(
            messages.filter(message => !isSystemMessage(message)),
        )
    }

    function clearSelectedMessages() {
        handleSetSelectedMessages([])
    }

    const canExport = selectedMessages.length > 0

    return (
        <ExportMessagesContext.Provider
            value={{
                isExporting,
                setIsExporting,
                selectedMessages,
                setSelectedMessages: handleSetSelectedMessages,
                selectAllMessages,
                selectManualMessages,
                clearSelectedMessages,
                canExport,
                handleCopy,
                handlePimsWriteback,
                cancelExport,
            }}
        >
            {children}
        </ExportMessagesContext.Provider>
    )
}

export function useExportMessages() {
    return useContext(ExportMessagesContext)
}
